import React, { useState } from 'react';
import { Typography, Alert, Card, Button, Space, Modal, Statistic, Row, Col, Tag } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import MolStarViewer from '../protein/structure/MolStarViewer';

const { Text } = Typography;

interface EquiDockResultProps {
  taskResult: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const EquiDockResult: React.FC<EquiDockResultProps> = ({ 
  taskResult, 
  data, 
  title = "EquiDock 对接结果", 
  showTitle = true 
}) => {
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedPdb, setSelectedPdb] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string>('');

  // 提取对接结果
  const getDockingResult = () => {
    // 格式1: Profile页面格式 - taskResult.result.results[].result
    if (taskResult?.result?.results?.[0]?.result) {
      return taskResult.result.results[0].result;
    }
    
    // 格式2: 前端页面格式 - 直接在taskResult中
    if (taskResult?.docked_ligand_pdb || taskResult?.complex_pdb) {
      return taskResult;
    }
    
    // 格式3: data参数
    if (data) {
      return data;
    }
    
    return null;
  };

  // 查看3D结构
  const handleView3D = (pdbContent: string, structureTitle: string) => {
    if (pdbContent && pdbContent.trim().length > 0) {
      setSelectedPdb(pdbContent);
      setViewerTitle(structureTitle);
      setViewerVisible(true);
    } else {
      alert('暂无PDB内容可查看');
    }
  };

  // 下载PDB文件
  const handleDownloadPdb = (pdbContent: string, filename: string) => {
    if (!pdbContent) {
      alert('暂无PDB内容可下载');
      return;
    }
    
    const blob = new Blob([pdbContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const result = getDockingResult();

  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }

  if (!result || (!result.docked_ligand_pdb && !result.complex_pdb)) {
    return (
      <Alert
        type="info"
        message="无EquiDock对接结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  const structureInfo = result.structure_info;

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
        </div>
      )}

      {/* 结构信息概览 */}
      {structureInfo && (
        <Card title="结构信息" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="总原子数" 
                value={structureInfo.total_atoms || 0} 
              />
            </Col>
            <Col span={6}>
              <div>
                <Text strong>受体链：</Text>
                <div style={{ marginTop: 4 }}>
                  {structureInfo.receptor_chains?.map((chain: string) => (
                    <Tag key={chain} color="blue">{chain}</Tag>
                  ))}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <Text strong>配体链：</Text>
                <div style={{ marginTop: 4 }}>
                  {structureInfo.ligand_chains?.map((chain: string) => (
                    <Tag key={chain} color="green">{chain}</Tag>
                  ))}
                </div>
              </div>
            </Col>
            <Col span={6}>
              <div>
                <Text strong>最终链：</Text>
                <div style={{ marginTop: 4 }}>
                  {structureInfo.final_chains?.map((chain: string) => (
                    <Tag key={chain} color="orange">{chain}</Tag>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* 对接结果操作 */}
      <Card title="对接结果" size="small">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          
          {/* 对接配体结构 */}
          {result.docked_ligand_pdb && (
            <div>
              <Text strong>对接配体结构</Text>
              <div style={{ marginTop: 8 }}>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<EyeOutlined />}
                    onClick={() => handleView3D(result.docked_ligand_pdb, 'EquiDock 对接配体')}
                  >
                    查看3D结构
                  </Button>
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadPdb(result.docked_ligand_pdb, 'equidock_docked_ligand.pdb')}
                  >
                    下载PDB
                  </Button>
                </Space>
              </div>
            </div>
          )}

          {/* 复合物结构 */}
          {result.complex_pdb && (
            <div>
              <Text strong>复合物结构</Text>
              <div style={{ marginTop: 8 }}>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<EyeOutlined />}
                    onClick={() => handleView3D(result.complex_pdb, 'EquiDock 复合物')}
                  >
                    查看3D结构
                  </Button>
                  <Button 
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadPdb(result.complex_pdb, 'equidock_complex.pdb')}
                  >
                    下载PDB
                  </Button>
                </Space>
              </div>
            </div>
          )}

          {/* Docker输出信息 */}
          {result.docker_output && (
            <div>
              <Text strong>执行日志</Text>
              <div style={{ 
                marginTop: 8, 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 4,
                maxHeight: 200,
                overflow: 'auto'
              }}>
                <pre style={{ margin: 0, fontSize: '12px' }}>
                  {result.docker_output}
                </pre>
              </div>
            </div>
          )}
        </Space>
      </Card>

      {/* 3D结构查看器模态框 */}
      <Modal
        title={viewerTitle}
        open={viewerVisible}
        onCancel={() => setViewerVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        {selectedPdb && (
          <div style={{ height: 600 }}>
            <MolStarViewer 
              pdbData={selectedPdb} 
              title={viewerTitle}
              height={580}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EquiDockResult;
